export type TypeDemandTrendConfidence = 'insufficient' | 'limited' | 'reliable';

const MAX_DEMAND_DEPTH_POINTS: number = 250;
const DAY = 86400000;
const MIN_COMPARABLE_DESIGNS = 5;

export interface IDemandScope {
	kind: 'loaded-results' | 'niche';
	searchTerm?: string;
	matchedPhrase?: string;
	searchFields?: string[];
	marketplace?: string;
	product?: string;
	limit?: number;
	truncated?: boolean;
}

export interface IDemandComparison {
	currentStart: string;
	currentEnd: string;
	previousStart: string;
	previousEnd: string;
	sampleSize: number;
	coverage: number;
	currentValue: number | null;
	previousValue: number | null;
	change: number | null;
	improvingDesigns: number;
	confidence: TypeDemandTrendConfidence;
	currentMedian: number | null;
	previousMedian: number | null;
	medianChange: number | null;
	maxDesignShare: number;
	singleDesignSensitive: boolean;
}

export interface IDemandSeasonality {
	peakMonth: number;
	years: number[];
	minimumDesignsPerYear: number;
	confidence: TypeDemandTrendConfidence;
	method: 'recurring-local-peak';
	evidence: Array<{ year: number;
		beforeSample: number;
		afterSample: number;
		rise: number;
		fall: number; }>;
}

export interface IDemandIndexPoint {
	x: Date;
	y: number | null;
	change: number | null;
	sampleSize: number;
	coverage: number;
	confidence: TypeDemandTrendConfidence;
	partial: boolean;
	referenceMonth: string | null;
	previousTotal: number | null;
	currentTotal: number | null;
	singleDesignSensitive: boolean;
}

export interface IDemandTrendDesign {
	asin?: string;
	product		: string;
	marketplace	: string;
	bsr?			: number;
	scrapedAt?	: string | Date | null;
	bsr_history?: Array<{
		rank: number;
		date: string | Date | null;
	}>;
}

export interface IDemandTrendPoint {
	x					: Date;
	y					: number | null;
	rawMedianSales		: number | null;
	lowerQuartileSales	: number | null;
	upperQuartileSales	: number | null;
	sampleSize			: number;
	coverage			: number;
	confidence: TypeDemandTrendConfidence;
	partial: boolean;
}

export interface IDemandDepthPoint {
	x		: number;
	y		: number;
	bsr		: number;
	position: number;
}

export interface IDemandCurrentSummary {
	medianSales			: number | null;
	upperQuartileSales	: number | null;
	rankedDesigns		: number;
	activeDesigns		: number;
}

export interface IDemandPeakComparison {
	currentYear		: number;
	previousYear	: number;
	currentValue	: number;
	previousValue	: number;
	change			: number | null;
	currentMonths	: number;
	previousMonths	: number;
}

export interface IDemandTrendAnalysis {
	methodVersion: 'matched-demand-v4';
	asOf: string;
	scope: IDemandScope;
	recentComparison: IDemandComparison;
	yearComparison: IDemandComparison;
	seasonality: IDemandSeasonality | null;
	undatedCurrentDesigns: number;
	staleCurrentDesigns: number;
	points					: IDemandTrendPoint[];
	indexPoints: IDemandIndexPoint[];
	loadedDesigns			: number;
	designsWithHistory		: number;
	requiredMonthlySample	: number;
	historicalPointCount	: number;
	latestPoint				: IDemandTrendPoint | null;
	yearOverYearChange		: number | null;
	peakComparison			: IDemandPeakComparison | null;
	improvingDesigns		: number;
	comparableDesigns		: number;
	confidence				: TypeDemandTrendConfidence;
	currentDepth			: IDemandDepthPoint[];
	currentSummary			: IDemandCurrentSummary;
}

interface IDesignMonthlyDemand {
	month	: number;
	rank	: number;
	sales	: number;
}

interface IMonthlyDemandBucket {
	sales: number[];
}

interface IObservation { time: number; rank: number }


/** Build a monthly history plus an always-available current demand profile. */
// Preserve the established coverage and confidence calculation during extraction.
// eslint-disable-next-line complexity
export function buildDemandTrendAnalysis(
	designs: IDemandTrendDesign[],
	estimateMonthlySales: (design: IDemandTrendDesign, bsr: number) => number,
	options: { asOf?: Date; scope?: IDemandScope } = {}
): IDemandTrendAnalysis {
	const asOf = options.asOf ?? new Date();
	if (!Number.isFinite(asOf.getTime())) throw new Error('Invalid demand analysis date');
	const currentMonth = getUtcMonthStart(asOf);
	// Identical ASINs from overlapping result pages must not receive extra weight.
	const seen: Set<string> = new Set();
	designs = designs.filter((design) => {
		if (!design.asin) return true;
		const key = `${design.marketplace}:${design.product}:${design.asin}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
	const loadedDesigns: number = designs.length;
	const designSeries: IDesignMonthlyDemand[][] = [];
	const datedDesigns: Array<{ design: IDemandTrendDesign; observations: IObservation[] }> = [];
	let undatedCurrentDesigns = 0;
	let staleCurrentDesigns = 0;

	for (const design of designs) {
		const observations = getObservations(design, asOf);
		datedDesigns.push({ design, observations });
		const ranksByMonth: Map<number, number[]> = new Map<number, number[]>();
		for (const observation of observations) {
			addRankToMonth(ranksByMonth, observation.rank, new Date(observation.time));
		}
		if (isValidRank(design.bsr)) {
			const timestamp = parseObservationTime(design.scrapedAt, asOf);
			if (timestamp === null) undatedCurrentDesigns++;
			else if (asOf.getTime() - timestamp > 30 * DAY) staleCurrentDesigns++;
		}

		const monthlyDemand: IDesignMonthlyDemand[] = Array.from(ranksByMonth.entries())
			.map(([month, ranks]: [number, number[]]) => {
				const rank: number = median(ranks);
				const sales: number = safelyEstimateSales(design, rank, estimateMonthlySales);
				return Number.isFinite(sales) && sales >= 0 ? { month, rank, sales } : null;
			})
			.filter((point: IDesignMonthlyDemand | null): point is IDesignMonthlyDemand => point !== null)
			.sort((first: IDesignMonthlyDemand, second: IDesignMonthlyDemand) => first.month - second.month);

		if (monthlyDemand.length > 0) designSeries.push(monthlyDemand);
	}

	const designsWithHistory: number = designSeries.length;
	// Visibility and confidence are separate: new designs cannot erase old observations.
	const requiredMonthlySample: number = 1;
	const buckets: Map<number, IMonthlyDemandBucket> = new Map<number, IMonthlyDemandBucket>();

	for (const series of designSeries) {
		for (const point of series) {
			const bucket: IMonthlyDemandBucket = buckets.get(point.month) ?? { sales: [] };
			bucket.sales.push(point.sales);
			buckets.set(point.month, bucket);
		}
	}

	const months: number[] = Array.from(buckets.keys()).sort((first: number, second: number) => first - second);
	const points: IDemandTrendPoint[] = [];
	if (months.length > 0 && requiredMonthlySample > 0) {
		for (let month: number = months[0]; month <= currentMonth; month = addUtcMonth(month)) {
			const sales: number[] = buckets.get(month)?.sales ?? [];
			if (sales.length < requiredMonthlySample) {
				points.push(createEmptyPoint(month, sales.length, loadedDesigns));
				continue;
			}

			points.push({
				x					: new Date(month),
				y					: median(sales),
				rawMedianSales		: median(sales),
				lowerQuartileSales	: percentile(sales, 0.25),
				upperQuartileSales	: percentile(sales, 0.75),
				sampleSize			: sales.length,
				coverage			: loadedDesigns > 0 ? sales.length / loadedDesigns : 0,
				confidence: sampleConfidence(sales.length, loadedDesigns),
				partial: month === currentMonth
			});
		}
	}

	const validPoints: IDemandTrendPoint[] = points.filter((point: IDemandTrendPoint) => point.y !== null);
	const latestPoint: IDemandTrendPoint | null = validPoints[validPoints.length - 1] ?? null;
	// Two equally long, explicit observation windows. Never compare different groups.
	// Observations are daily UTC buckets, so use matching whole-day boundaries.
	const end = Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), asOf.getUTCDate() + 1);
	const recentComparison = compareWindows(datedDesigns, estimateMonthlySales, end - 30 * DAY, end, end - 60 * DAY, end - 30 * DAY);
	const yearComparison = compareYearToDate(datedDesigns, estimateMonthlySales, asOf);
	const currentDepth: IDemandDepthPoint[] = buildCurrentDepth(designs, estimateMonthlySales);
	const currentSales: number[] = currentDepth.map((point: IDemandDepthPoint) => point.y);
	const confidence: TypeDemandTrendConfidence = getConfidence(loadedDesigns, latestPoint, validPoints.length);

	return {
		methodVersion: 'matched-demand-v4',
		asOf: asOf.toISOString(),
		scope: options.scope ?? { kind: 'loaded-results' },
		recentComparison,
		yearComparison,
		seasonality: calculateSeasonality(designSeries, asOf, loadedDesigns),
		indexPoints: buildMatchedIndex(designSeries, points, loadedDesigns),
		undatedCurrentDesigns,
		staleCurrentDesigns,
		points,
		loadedDesigns,
		designsWithHistory,
		requiredMonthlySample,
		historicalPointCount	: validPoints.length,
		latestPoint,
		yearOverYearChange		: yearComparison.change,
		peakComparison			: null,
		improvingDesigns		: recentComparison.improvingDesigns,
		comparableDesigns		: recentComparison.sampleSize,
		confidence,
		currentDepth			: downsampleDemandDepth(currentDepth),
		currentSummary: {
			medianSales			: currentSales.length > 0 ? median(currentSales) : null,
			upperQuartileSales	: currentSales.length > 0 ? percentile(currentSales, 0.75) : null,
			rankedDesigns		: currentDepth.length,
			activeDesigns		: currentSales.filter((sales: number) => sales >= 1).length
		}
	};
}


function addRankToMonth(ranksByMonth: Map<number, number[]>, rankValue: number, dateValue: string | Date): void {
	const rank: number = Number(rankValue);
	const date: Date = new Date(dateValue);
	if (!isValidRank(rank) || Number.isNaN(date.getTime())) return;

	const month: number = getUtcMonthStart(date);
	const monthlyRanks: number[] = ranksByMonth.get(month) ?? [];
	monthlyRanks.push(rank);
	ranksByMonth.set(month, monthlyRanks);
}

function isValidRank(rank: number | undefined): rank is number {
	return typeof rank === 'number' && Number.isFinite(rank) && rank > 0 && rank < 99999999;
}

function parseObservationTime(value: string | Date | null | undefined, asOf: Date): number | null {
	if (!value) return null;
	const time = new Date(value).getTime();
	return Number.isFinite(time) && time >= Date.UTC(2000, 0, 1) && time <= asOf.getTime() ? time : null;
}

function getObservations(design: IDemandTrendDesign, asOf: Date): IObservation[] {
	const observations: Map<number, number> = new Map();
	for (const point of design.bsr_history ?? []) {
		const time = parseObservationTime(point?.date, asOf);
		if (time !== null && isValidRank(point?.rank)) observations.set(time, point.rank);
	}
	// Undated current values are useful for a snapshot, never for dated history.
	const currentTime = parseObservationTime(design.scrapedAt, asOf);
	if (currentTime !== null && isValidRank(design.bsr)) observations.set(currentTime, design.bsr);
	// Daily medians prevent frequently scraped days from dominating a monthly value.
	const days: Map<number, number[]> = new Map();
	for (const [time, rank] of observations) {
		const day = Math.floor(time / DAY) * DAY;
		const ranks = days.get(day) ?? [];
		ranks.push(rank);
		days.set(day, ranks);
	}
	return Array.from(days, ([time, ranks]) => ({ time, rank: median(ranks) }));
}

function safelyEstimateSales(
	design: IDemandTrendDesign,
	bsr: number,
	estimateMonthlySales: (design: IDemandTrendDesign, bsr: number) => number
): number {
	try {
		return estimateMonthlySales(design, bsr);
	} catch {
		return Number.NaN;
	}
}

function buildCurrentDepth(
	designs: IDemandTrendDesign[],
	estimateMonthlySales: (design: IDemandTrendDesign, bsr: number) => number
): IDemandDepthPoint[] {
	const rankedDesigns: Array<{ bsr: number; sales: number }> = designs
		.map((design: IDemandTrendDesign) => {
			const bsr: number = Number(design.bsr);
			const sales: number = safelyEstimateSales(design, bsr, estimateMonthlySales);
			return { bsr, sales };
		})
		.filter((design: { bsr: number; sales: number }) => isValidRank(design.bsr)
		&& Number.isFinite(design.sales)
		&& design.sales >= 0
		)
		.sort((first: { bsr: number;
			sales: number; }, second: { bsr: number;
			sales: number; }) => second.sales - first.sales);

	return rankedDesigns.map((design: { bsr: number; sales: number }, index: number) => ({
		x		: (index + 1) / rankedDesigns.length * 100,
		y		: design.sales,
		bsr		: design.bsr,
		position: index + 1
	}));
}

function downsampleDemandDepth(points: IDemandDepthPoint[]): IDemandDepthPoint[] {
	if (points.length <= MAX_DEMAND_DEPTH_POINTS) return points;

	const sampledPoints: IDemandDepthPoint[] = [];
	for (let index: number = 0; index < MAX_DEMAND_DEPTH_POINTS; index += 1) {
		const sourceIndex: number = Math.round(index * (points.length - 1) / (MAX_DEMAND_DEPTH_POINTS - 1));
		sampledPoints.push(points[sourceIndex]);
	}
	return sampledPoints;
}

function createEmptyPoint(month: number, sampleSize: number, loadedDesigns: number): IDemandTrendPoint {
	return {
		x					: new Date(month),
		y					: null,
		rawMedianSales		: null,
		lowerQuartileSales	: null,
		upperQuartileSales	: null,
		sampleSize,
		coverage			: loadedDesigns > 0 ? sampleSize / loadedDesigns : 0,
		confidence: 'insufficient',
		partial: false
	};
}

function compareWindows(
	designs: Array<{ design: IDemandTrendDesign; observations: IObservation[] }>,
	estimate: (design: IDemandTrendDesign, rank: number) => number,
	currentStart: number, currentEnd: number, previousStart: number, previousEnd: number
): IDemandComparison {
	const current: number[] = [];
	const previous: number[] = [];
	let improvingDesigns = 0;
	for (const { design, observations } of designs) {
		const currentRanks = observations.filter((p) => p.time >= currentStart && p.time < currentEnd).map((p) => p.rank);
		const previousRanks = observations.filter((p) => p.time >= previousStart && p.time < previousEnd).map((p) => p.rank);
		if (!currentRanks.length || !previousRanks.length) continue;
		const a = safelyEstimateSales(design, median(currentRanks), estimate);
		const b = safelyEstimateSales(design, median(previousRanks), estimate);
		if (!Number.isFinite(a) || !Number.isFinite(b) || a < 0 || b < 0) continue;
		current.push(a);
		previous.push(b);
		if (a > b) improvingDesigns++;
	}
	return {
		currentStart: new Date(currentStart).toISOString(), currentEnd: new Date(currentEnd - 1).toISOString(),
		previousStart: new Date(previousStart).toISOString(), previousEnd: new Date(previousEnd - 1).toISOString(),
		...summarizePairs(current, previous, designs.length), improvingDesigns
	};
}

/** Match calendar months within each design so sparse seasonal coverage cannot skew YTD. */
function compareYearToDate(
	designs: Array<{ design: IDemandTrendDesign; observations: IObservation[] }>,
	estimate: (design: IDemandTrendDesign, rank: number) => number,
	asOf: Date
): IDemandComparison {
	const year = asOf.getUTCFullYear(),
		month = asOf.getUTCMonth();
	const day = asOf.getUTCDate();
	const previousDay = Math.min(day, new Date(Date.UTC(year - 1, month + 1, 0)).getUTCDate());
	const currentStart = Date.UTC(year, 0, 1),
		previousStart = Date.UTC(year - 1, 0, 1);
	const currentEnd = Date.UTC(year, month, day + 1),
		previousEnd = Date.UTC(year - 1, month, previousDay + 1);
	const current: number[] = [],
		previous: number[] = [];
	let improvingDesigns = 0;
	for (const { design, observations } of designs) {
		const currentMonths: Map<number, number[]> = new Map(),
			previousMonths: Map<number, number[]> = new Map();
		for (const point of observations) {
			const bucket = point.time >= currentStart && point.time < currentEnd
				? currentMonths
				: point.time >= previousStart && point.time < previousEnd ? previousMonths : null;
			if (!bucket) continue;
			const m = new Date(point.time).getUTCMonth();
			const ranks = bucket.get(m) ?? [];
			ranks.push(point.rank); bucket.set(m, ranks);
		}
		let a = 0,
			b = 0,
			matchedMonths = 0;
		for (const [m, ranks] of currentMonths) {
			const prior = previousMonths.get(m);
			if (!prior?.length) continue;
			const x = safelyEstimateSales(design, median(ranks), estimate);
			const y = safelyEstimateSales(design, median(prior), estimate);
			if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 0) continue;
			a += x;
			b += y;
			matchedMonths++;
		}
		if (!matchedMonths) continue;
		// A frequently observed design must not gain extra weight just from more months.
		current.push(a / matchedMonths); previous.push(b / matchedMonths);
		if (a > b) improvingDesigns++;
	}
	return {
		currentStart: new Date(currentStart).toISOString(), currentEnd: new Date(currentEnd - 1).toISOString(),
		previousStart: new Date(previousStart).toISOString(), previousEnd: new Date(previousEnd - 1).toISOString(),
		...summarizePairs(current, previous, designs.length), improvingDesigns
	};
}

/** Summed estimated rates answer demand-volume change; medians answer typical-design change. */
function summarizePairs(current: number[], previous: number[], total: number) {
	const currentValue = current.length ? current.reduce((a, b) => a + b, 0) : null;
	const previousValue = previous.length ? previous.reduce((a, b) => a + b, 0) : null;
	const currentMedian = current.length ? median(current) : null;
	const previousMedian = previous.length ? median(previous) : null;
	const enough = current.length >= MIN_COMPARABLE_DESIGNS;
	const maxDesignShare = current.length
		? Math.max(...current.map((v, i) => Math.max(
			currentValue ? v / currentValue : 0, previousValue ? previous[i] / previousValue : 0)))
		: 0;
	const delta = (currentValue ?? 0) - (previousValue ?? 0);
	const singleDesignSensitive = current.some((v, i) => Math.sign(delta - (v - previous[i])) !== Math.sign(delta));
	return { currentValue, previousValue, currentMedian, previousMedian, maxDesignShare, singleDesignSensitive,
		change: enough && previousValue !== null && previousValue > 0 && maxDesignShare <= 0.5
			? ((currentValue!) / previousValue - 1) * 100
			: null,
		medianChange: enough && previousMedian !== null && previousMedian > 0
			? ((currentMedian!) / previousMedian - 1) * 100
			: null,
		sampleSize: current.length, coverage: total ? current.length / total : 0,
		confidence: sampleConfidence(current.length, total) };
}

/** Adjacent-month links never include a design observed in only one side of the link. */
function monthlyPairs(series: IDesignMonthlyDemand[][]): Map<number, { current: number[]; previous: number[] }> {
	const pairs: Map<number, { current: number[]; previous: number[] }> = new Map();
	for (const design of series) for (let i = 1; i < design.length; i++) {
		const a = design[i - 1],
			b = design[i];
		if (addUtcMonth(a.month) !== b.month) continue;
		const pair = pairs.get(b.month) ?? { current: [], previous: [] };
		pair.current.push(b.sales);
		pair.previous.push(a.sales);
		pairs.set(b.month, pair);
	}
	return pairs;
}

function buildMatchedIndex(series: IDesignMonthlyDemand[][], observed: IDemandTrendPoint[], total: number): IDemandIndexPoint[] {
	// A direct fixed-reference index avoids compounding links from different cohorts.
	const reference = [...observed].reverse().find((p) => p.y !== null);
	const pairs: Map<number, { current: number[]; previous: number[] }> = new Map();
	if (reference) for (const design of series) {
		const baseline = design.find((p) => p.month === reference.x.getTime());
		if (!baseline) continue;
		for (const point of design) {
			const pair = pairs.get(point.month) ?? { current: [], previous: [] };
			pair.current.push(point.sales);
			pair.previous.push(baseline.sales);
			pairs.set(point.month, pair);
		}
	}
	return observed.map((p) => {
		const pair = pairs.get(p.x.getTime()) ?? { current: [], previous: [] };
		const stats = summarizePairs(pair.current, pair.previous, total);
		const isReference = p.x.getTime() === reference?.x.getTime();
		return { x: p.x, y: stats.change === null ? null : 100 + stats.change,
			change: isReference ? null : stats.change, sampleSize: stats.sampleSize, coverage: stats.coverage,
			confidence: stats.confidence, partial: p.partial || Boolean(reference?.partial),
			referenceMonth: reference?.x.toISOString() ?? null,
			previousTotal: stats.previousValue, currentTotal: stats.currentValue,
			singleDesignSensitive: stats.singleDesignSensitive };
	});
}

function sampleConfidence(sample: number, total: number): TypeDemandTrendConfidence {
	if (sample < MIN_COMPARABLE_DESIGNS) return 'insufficient';
	return sample >= 20 && total > 0 && sample / total >= 0.2 ? 'reliable' : 'limited';
}

/** A recurring local peak needs independently supported rises AND falls in multiple years. */
function calculateSeasonality(series: IDesignMonthlyDemand[][], asOf: Date, loadedDesigns: number): IDemandSeasonality | null {
	const pairs = monthlyPairs(series);
	const candidates: Array<IDemandSeasonality & { strength: number }> = [];
	for (let month = 0; month < 12; month++) {
		const evidence: IDemandSeasonality['evidence'] = [];
		let eligibleYears = 0;
		for (let year = asOf.getUTCFullYear() - 3; year < asOf.getUTCFullYear(); year++) {
			const start = Date.UTC(year, month, 1),
				afterMonth = addUtcMonth(start);
			// December needs the following January to be complete too.
			if (addUtcMonth(afterMonth) > getUtcMonthStart(asOf)) continue;
			const before = pairs.get(start),
				after = pairs.get(afterMonth);
			if (!before || !after || before.current.length < 20 || after.current.length < 20) continue;
			const rise = summarizePairs(before.current, before.previous, loadedDesigns);
			const fall = summarizePairs(after.current, after.previous, loadedDesigns);
			if (rise.change === null || fall.change === null) continue;
			eligibleYears++;
			// Reject weak bumps and peaks whose direction depends on a single design.
			if (rise.change < 10 || fall.change > -100 / 11 || rise.singleDesignSensitive || fall.singleDesignSensitive) continue;
			evidence.push({ year, beforeSample: rise.sampleSize, afterSample: fall.sampleSize,
				rise: rise.change, fall: fall.change });
		}
		if (evidence.length < 2 || evidence.length <= eligibleYears / 2) continue;
		const minimumDesignsPerYear = Math.min(...evidence.flatMap((evidencePoint) => [evidencePoint.beforeSample, evidencePoint.afterSample]));
		candidates.push({ peakMonth: month + 1, years: evidence.map((evidencePoint) => evidencePoint.year), evidence,
			method: 'recurring-local-peak', minimumDesignsPerYear,
			confidence: sampleConfidence(minimumDesignsPerYear, loadedDesigns),
			strength: median(evidence.map((evidencePoint) => Math.min(Math.log1p(evidencePoint.rise / 100), -Math.log1p(evidencePoint.fall / 100)))) });
	}
	candidates.sort((a, b) => b.years.length - a.years.length || b.strength - a.strength);
	if (!candidates.length) return null;
	// Similar competing peaks do not justify a single-month claim.
	if (candidates[1]?.years.length === candidates[0].years.length && candidates[0].strength < candidates[1].strength * 1.1) return null;
	const { strength, ...result } = candidates[0];
	return result;
}

/** Restore chart dates after JSON transport; numerical results are not recomputed by the UI. */
export function hydrateDemandAnalysis(analysis: IDemandTrendAnalysis): IDemandTrendAnalysis {
	return { ...analysis, points: analysis.points.map((point) => ({ ...point, x: new Date(point.x) })),
		indexPoints: analysis.indexPoints.map((point) => ({ ...point, x: new Date(point.x) })),
		latestPoint: analysis.latestPoint ? { ...analysis.latestPoint, x: new Date(analysis.latestPoint.x) } : null };
}

function getConfidence(
	loadedDesigns: number,
	latestPoint: IDemandTrendPoint | null,
	historicalPointCount: number
): TypeDemandTrendConfidence {
	if (!latestPoint || historicalPointCount < 2) return 'insufficient';
	return sampleConfidence(latestPoint.sampleSize, loadedDesigns);
}

function getUtcMonthStart(date: Date): number {
	return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
}

function addUtcMonth(month: number): number {
	const date: Date = new Date(month);
	return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
}

function median(values: number[]): number {
	return percentile(values, 0.5);
}

function percentile(values: number[], percentileValue: number): number {
	if (values.length === 0) return 0;
	const sorted: number[] = [...values].sort((first: number, second: number) => first - second);
	const position: number = (sorted.length - 1) * percentileValue;
	const lowerIndex: number = Math.floor(position);
	const upperIndex: number = Math.ceil(position);
	if (lowerIndex === upperIndex) return sorted[lowerIndex];
	const weight: number = position - lowerIndex;
	return sorted[lowerIndex] + ((sorted[upperIndex] - sorted[lowerIndex]) * weight);
}
